import { isIntakePath } from './publicIntakeScope';

/*
  Pure decision for the PWA update gate, kept free of the `virtual:pwa-register`
  and react-router imports so it's unit-testable without a service worker.

  We never apply a waiting service worker the moment it's detected (a hard reload
  mid-session is jarring and — worst case — can land on the OIDC `/callback` while
  the single-use auth code is being exchanged, breaking sign-in). Instead the new
  SW waits and is applied at the user's next real navigation.

  Used by the router-navigation handler in pwaUpdate.ts.
*/
export function shouldApplyOnNavigation(opts: {
  needRefresh: boolean;
  isDirty: boolean;
  historyAction: 'PUSH' | 'REPLACE' | 'POP';
  pathname: string;
}): boolean {
  if (!opts.needRefresh) return false; // nothing waiting
  if (opts.isDirty) return false; // never reload over unsaved edits
  // Only user-initiated forward navigations. REPLACE covers the programmatic
  // post-login redirect (Callback.tsx) and other in-place swaps; POP is back/fwd.
  if (opts.historyAction !== 'PUSH') return false;
  // Defensive: never reload on the auth routes even on a PUSH.
  if (opts.pathname === '/callback' || opts.pathname === '/login') return false;
  return true;
}

/**
 * Should a freshly detected update be applied straight away, without waiting for a
 * navigation? Only on the public intake form, and only while it is clean (#721).
 *
 * The wait-for-a-PUSH rule above is right for the authenticated app and wrong here,
 * for a reason specific to this surface rather than a preference: the intake wizard
 * is a single route whose steps are component state, so a PUSH never comes and the
 * waiting worker is never applied at all. Denylisting `/intake` from the precached
 * shell (vite.config.ts) is the fix for that, but it only takes effect once the
 * worker CARRYING it is in control — which is what this rule does.
 *
 * There is no auth ceremony to interrupt: this route is public, sits outside
 * ProtectedRoute, and can never be `/callback`. What IS worth protecting takes two
 * signals, because neither one covers the other:
 *
 *   `isDirty`  — the client is typing. `GuardedForm` latches on the first `input`,
 *                so a client fifteen minutes into the form is never reloaded out of
 *                it. But the latch is per-mount and resets on reload, and Back /
 *                Next / the section navigator / Send fire no `input` event at all —
 *                so a client who RESUMED a draft and only clicks reads clean.
 *   `isReloadBlocked` — the wizard says a reload would destroy something regardless
 *                of typing: a submission in flight (reloading mid-POST loses the
 *                outcome and invites a duplicate send), or the confirmation screen
 *                (reloading it drops the client back to an empty form, with the
 *                draft already cleared and no evidence their answers arrived).
 *
 * The second is why this is not gated on `isDirty` alone, and an earlier revision of
 * this function that was got both wrong.
 */
export function shouldApplyOnDetection(opts: {
  isDirty: boolean;
  isReloadBlocked: boolean;
  pathname: string;
}): boolean {
  return isIntakePath(opts.pathname) && !opts.isDirty && !opts.isReloadBlocked;
}
